<template>
  <div class="returns-page">
    <div class="page-header">
      <div>
        <h2>Return Analysis</h2>
        <p>Track your investment return percentage by year, half-year, quarter, and month.</p>
      </div>

      <div class="snapshot-card">
        <span class="snapshot-label">Absolute Return</span>
        <strong :class="returnClass(latestSnapshot.returnRate)">
          {{ $helper.formatPercentage(latestSnapshot.returnRate) }}
        </strong>
        <small>
          {{ $helper.formatPrice(latestSnapshot.profitLoss) }} on
          {{ $helper.formatPrice(latestSnapshot.investment) }}
        </small>
      </div>
    </div>

    <div class="summary-grid">
      <div class="metric-card">
        <span>Total Investment</span>
        <strong>{{ $helper.formatPrice(latestSnapshot.investment) }}</strong>
      </div>
      <div class="metric-card">
        <span>Profit / Loss</span>
        <strong :class="returnClass(latestSnapshot.returnRate)">
          {{ $helper.formatPrice(latestSnapshot.profitLoss) }}
        </strong>
      </div>
      <div class="metric-card">
        <span>Total Value</span>
        <strong>{{ $helper.formatPrice(latestSnapshot.totalValue) }}</strong>
      </div>
      <div class="metric-card">
        <span>History Points</span>
        <strong>{{ normalizedHistory.length }}</strong>
      </div>
    </div>

    <div class="summary-grid summary-grid-secondary">
      <div class="metric-card">
        <span>Portfolio CAGR</span>
        <strong :class="returnClass(portfolioCagr.rate)">
          {{ formatRate(portfolioCagr.rate) }}
        </strong>
        <small>Based on first vs latest portfolio value snapshot</small>
      </div>
      <div class="metric-card">
        <span>XIRR</span>
        <strong :class="returnClass(annualizedMetrics.xirr)">
          {{ formatRate(annualizedMetrics.xirr) }}
        </strong>
        <small>Based on grouped buy, sell, dividend cash flows plus current value</small>
      </div>
      <div class="metric-card">
        <span>Cash Flow Rows</span>
        <strong>{{ annualizedMetrics.auditCount || '-' }}</strong>
        <small>Grouped daily rows used for XIRR</small>
      </div>
    </div>

    <div class="audit-toggle-row">
      <label class="audit-toggle">
        <input v-model="showXirrAudit" type="checkbox">
        <span>Show XIRR audit rows</span>
      </label>
    </div>

    <div v-if="showXirrAudit" class="table-card">
      <div class="table-header">
        <div>
          <h3>XIRR Audit</h3>
          <p class="table-note">These are the grouped cash-flow rows currently used in the XIRR calculation.</p>
        </div>
      </div>

      <div v-if="!xirrAuditRows.length" class="empty-state">
        No audit rows available.
      </div>

      <div v-else class="period-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Cash Flow</th>
              <th>Direction</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in xirrAuditRows" :key="row.key">
              <td>{{ formatShortDate(row.date) }}</td>
              <td>{{ row.type }}</td>
              <td :class="returnClass(row.amount)">
                {{ $helper.formatPrice(row.amount) }}
              </td>
              <td>{{ row.amount >= 0 ? 'Inflow' : 'Outflow' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="table-card">
      <div class="table-header">
        <div>
          <h3>Period Wise Returns</h3>
          <p class="table-note">Use XIRR for FD/index-style comparison. Portfolio CAGR is based on portfolio value growth and is not cash-flow adjusted.</p>
          <p>Return % = period market gain/loss divided by opening portfolio value. Investment shows fresh capital added in that period.</p>
        </div>
      </div>

      <div v-if="$apollo.queries.statisticsChart.loading || $apollo.queries.statisticsChartPlatformWise.loading" class="empty-state">
        Loading return history...
      </div>

      <div v-else-if="!groupedReturns.length" class="empty-state">
        No return history available yet.
      </div>

      <div v-else class="period-table">
        <table>
          <thead>
            <tr>
              <th>Period</th>
              <th>Investment</th>
              <th>Profit / Loss</th>
              <th>Return %</th>
              <th>Total Value</th>
              <th>As Of</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="year in groupedReturns" :key="year.key">
              <tr class="group-row level-year">
                <td>{{ year.label }}</td>
                <td>{{ $helper.formatPrice(year.investment) }}</td>
                <td :class="returnClass(year.returnRate)">{{ $helper.formatPrice(year.profitLoss) }}</td>
                <td :class="returnClass(year.returnRate)">{{ $helper.formatPercentage(year.returnRate) }}</td>
                <td>{{ $helper.formatPrice(year.totalValue) }}</td>
                <td>{{ formatShortDate(year.updatedAt) }}</td>
              </tr>

              <template v-for="half in year.children" :key="half.key">
                <tr class="group-row level-half">
                  <td>{{ half.label }}</td>
                  <td>{{ $helper.formatPrice(half.investment) }}</td>
                  <td :class="returnClass(half.returnRate)">{{ $helper.formatPrice(half.profitLoss) }}</td>
                  <td :class="returnClass(half.returnRate)">{{ $helper.formatPercentage(half.returnRate) }}</td>
                  <td>{{ $helper.formatPrice(half.totalValue) }}</td>
                  <td>{{ formatShortDate(half.updatedAt) }}</td>
                </tr>

                <template v-for="quarter in half.children" :key="quarter.key">
                  <tr class="group-row level-quarter">
                    <td>{{ quarter.label }}</td>
                    <td>{{ $helper.formatPrice(quarter.investment) }}</td>
                    <td :class="returnClass(quarter.returnRate)">{{ $helper.formatPrice(quarter.profitLoss) }}</td>
                    <td :class="returnClass(quarter.returnRate)">{{ $helper.formatPercentage(quarter.returnRate) }}</td>
                    <td>{{ $helper.formatPrice(quarter.totalValue) }}</td>
                    <td>{{ formatShortDate(quarter.updatedAt) }}</td>
                  </tr>

                  <tr
                    v-for="month in quarter.children"
                    :key="month.key"
                    class="detail-row level-month"
                  >
                    <td>{{ month.label }}</td>
                    <td>{{ $helper.formatPrice(month.investment) }}</td>
                    <td :class="returnClass(month.returnRate)">{{ $helper.formatPrice(month.profitLoss) }}</td>
                    <td :class="returnClass(month.returnRate)">{{ $helper.formatPercentage(month.returnRate) }}</td>
                    <td>{{ $helper.formatPrice(month.totalValue) }}</td>
                    <td>{{ formatShortDate(month.updatedAt) }}</td>
                  </tr>
                </template>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { platformStore } from '@/stores';

const toTimestamp = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && String(value).trim() !== '') {
    return numericValue < 1000000000000 ? numericValue * 1000 : numericValue;
  }

  const normalizedValue = typeof value === 'string'
    ? value.replace(' ', 'T')
    : value;
  const parsedValue = Date.parse(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const STATISTICS_CHART_QUERY = gql`
  query StatisticsChart($platformIds: [ID]) {
    statisticsChart(platform_ids: $platformIds) {
      stock_investment
      stock_profit_loss
      stock_total
      updated_at
    }
  }
`;

const STATISTICS_CHART_PLATFORM_WISE = gql`
  query StatisticsChartPlatformWise($platformIds: [ID]) {
    statisticsChartPlatformWise(platform_ids: $platformIds) {
      name
      investment
      profit_loss
      updated_at
      statistics {
        stock_investment
        stock_profit_loss
        stock_total
        updated_at
      }
    }
  }
`;

const STOCK_TRANSACTIONS_PLATFORM_WISE = gql`
  query StockTransactionsPlatformWise($platformIds: [ID]) {
    stockTransactionsPlatformWise(platform_ids: $platformIds) {
      stock_transaction_id
      platform_id
      action
      sub_total
      created_at
    }
  }
`;

const ALL_DIVIDENDS = gql`
  query Dividends {
    dividends {
      dividend_id
      platform_id
      amount
      created_at
    }
  }
`;

export default {
  name: 'PerformanceReturns',

  data() {
    return {
      showXirrAudit: false,
      statisticsChart: [],
      statisticsChartPlatformWise: [],
      stockTransactionsPlatformWise: [],
      dividends: []
    };
  },

  computed: {
    normalizedHistory() {
      const historyMap = new Map();
      const rows = Array.isArray(this.statisticsChart) ? this.statisticsChart : [];

      const addRow = (updatedAtValue, investmentValue, profitLossValue, totalValue) => {
        const timestamp = toTimestamp(updatedAtValue);
        if (!Number.isFinite(timestamp)) {
          return;
        }

        const current = historyMap.get(timestamp) || {
          timestamp,
          updatedAt: updatedAtValue,
          investment: 0,
          profitLoss: 0,
          totalValue: 0
        };

        current.investment += Number(investmentValue) || 0;
        current.profitLoss += Number(profitLossValue) || 0;
        current.totalValue += Number(totalValue) || 0;
        current.updatedAt = updatedAtValue;
        historyMap.set(timestamp, current);
      };

      rows.forEach((row) => {
        addRow(row.updated_at, row.stock_investment, row.stock_profit_loss, row.stock_total);
      });

      return Array.from(historyMap.values())
        .map((row) => ({
          ...row,
          totalValue: row.totalValue || row.investment + row.profitLoss,
          returnRate: row.investment ? row.profitLoss / row.investment : 0
        }))
        .sort((a, b) => a.timestamp - b.timestamp);
    },

    latestSnapshot() {
      return this.normalizedHistory[this.normalizedHistory.length - 1] || {
        investment: 0,
        profitLoss: 0,
        totalValue: 0,
        returnRate: 0,
        updatedAt: null
      };
    },

    portfolioCagr() {
      const history = this.normalizedHistory.filter((row) => Number(row.totalValue) > 0);
      if (history.length < 2) {
        return { rate: null, yearsHeld: null };
      }

      const first = history[0];
      const last = history[history.length - 1];
      const firstValue = Number(first.totalValue) || 0;
      const lastValue = Number(last.totalValue) || 0;
      const yearsHeld = (last.timestamp - first.timestamp) / (365.25 * 24 * 60 * 60 * 1000);

      if (firstValue <= 0 || lastValue <= 0 || yearsHeld <= 0) {
        return { rate: null, yearsHeld };
      }

      return {
        rate: Math.pow(lastValue / firstValue, 1 / yearsHeld) - 1,
        yearsHeld
      };
    },

    selectedPlatformIds() {
      return this.parsePlatformOptions().map((id) => String(id));
    },

    filteredDividends() {
      const selectedIds = new Set(this.selectedPlatformIds);
      return (Array.isArray(this.dividends) ? this.dividends : []).filter((row) => {
        if (!selectedIds.size || selectedIds.has('0')) {
          return true;
        }
        return selectedIds.has(String(row.platform_id));
      });
    },

    investmentCashFlows() {
      const transactionFlows = (Array.isArray(this.stockTransactionsPlatformWise) ? this.stockTransactionsPlatformWise : [])
        .map((row) => {
          const action = Number(row.action);
          const subTotal = Number(row.sub_total) || 0;
          const timestamp = toTimestamp(row.created_at);

          if (!Number.isFinite(timestamp) || !subTotal) {
            return null;
          }

          let amount = 0;
          if (action === 1 || action === 3) {
            amount = -subTotal;
          } else if (action === 2) {
            amount = subTotal;
          }

          return amount ? { amount, timestamp, date: new Date(timestamp) } : null;
        })
        .filter(Boolean);

      const dividendFlows = this.filteredDividends
        .map((row) => {
          const amount = Number(row.amount) || 0;
          const timestamp = toTimestamp(row.created_at);

          if (!Number.isFinite(timestamp) || !amount) {
            return null;
          }

          return { amount, timestamp, date: new Date(timestamp) };
        })
        .filter(Boolean);

      const grouped = new Map();

      [...transactionFlows, ...dividendFlows].forEach((flow) => {
        const dayKey = new Date(flow.timestamp).toISOString().slice(0, 10);
        const current = grouped.get(dayKey) || {
          amount: 0,
          timestamp: flow.timestamp,
          date: new Date(dayKey)
        };

        current.amount += flow.amount;
        current.timestamp = Math.min(current.timestamp, flow.timestamp);
        grouped.set(dayKey, current);
      });

      return Array.from(grouped.values())
        .filter((flow) => Math.abs(flow.amount) > 0.0001)
        .sort((a, b) => a.timestamp - b.timestamp);
    },

    annualizedMetrics() {
      const baseFlows = [...this.investmentCashFlows];
      const endingValue = Number(this.latestSnapshot.totalValue) || 0;
      const latestTimestamp = toTimestamp(this.latestSnapshot.updatedAt);

      if (!baseFlows.length || !Number.isFinite(latestTimestamp) || !endingValue) {
        return { xirr: null, yearsHeld: null, auditCount: 0 };
      }

      const xirrFlows = [
        ...baseFlows,
        { amount: endingValue, timestamp: latestTimestamp, date: new Date(latestTimestamp) }
      ];

      const firstTimestamp = baseFlows[0].timestamp;
      const yearsHeld = (latestTimestamp - firstTimestamp) / (365.25 * 24 * 60 * 60 * 1000);

      return {
        xirr: this.calculateXirr(xirrFlows),
        yearsHeld,
        auditCount: baseFlows.length
      };
    },

    xirrAuditRows() {
      const cashFlowRows = this.investmentCashFlows.map((flow, index) => ({
        key: `flow-${index}-${flow.timestamp}`,
        date: flow.timestamp,
        type: flow.amount >= 0 ? 'Sell/Dividend' : 'Buy',
        amount: flow.amount
      }));

      const latestTimestamp = toTimestamp(this.latestSnapshot.updatedAt);
      const endingValue = Number(this.latestSnapshot.totalValue) || 0;

      if (Number.isFinite(latestTimestamp) && endingValue) {
        cashFlowRows.push({
          key: `ending-${latestTimestamp}`,
          date: latestTimestamp,
          type: 'Current Value',
          amount: endingValue
        });
      }

      return cashFlowRows.sort((a, b) => a.date - b.date);
    },

    groupedReturns() {
      const years = new Map();

      const createNode = (key, label) => ({
        key,
        label,
        rows: [],
        children: new Map()
      });

      const addSnapshot = (node, row) => {
        node.rows.push(row);
        node.snapshot = row;
      };

      this.normalizedHistory.forEach((row) => {
        const date = new Date(row.timestamp);
        const year = date.getFullYear();
        const monthNumber = date.getMonth() + 1;
        const monthIndex = date.getMonth();
        const quarterNumber = Math.floor(monthIndex / 3) + 1;
        const halfNumber = monthNumber <= 6 ? 1 : 2;

        if (!years.has(year)) {
          years.set(year, createNode(`year-${year}`, String(year)));
        }

        const yearNode = years.get(year);
        addSnapshot(yearNode, row);

        const halfKey = `${year}-H${halfNumber}`;
        if (!yearNode.children.has(halfKey)) {
          yearNode.children.set(halfKey, createNode(`half-${halfKey}`, `Half Year ${halfNumber}`));
        }

        const halfNode = yearNode.children.get(halfKey);
        addSnapshot(halfNode, row);

        const quarterKey = `${year}-Q${quarterNumber}`;
        if (!halfNode.children.has(quarterKey)) {
          halfNode.children.set(quarterKey, createNode(`quarter-${quarterKey}`, `Quarter ${quarterNumber}`));
        }

        const quarterNode = halfNode.children.get(quarterKey);
        addSnapshot(quarterNode, row);

        const monthKey = `${year}-${String(monthNumber).padStart(2, '0')}`;
        if (!quarterNode.children.has(monthKey)) {
          quarterNode.children.set(
            monthKey,
            createNode(
              `month-${monthKey}`,
              new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(date)
            )
          );
        }

        addSnapshot(quarterNode.children.get(monthKey), row);
      });

      const mapNode = (node, sortChildren) => {
        const sortedRows = [...(node.rows || [])].sort((a, b) => a.timestamp - b.timestamp);
        const openingSnapshot = sortedRows[0] || null;
        const closingSnapshot = sortedRows[sortedRows.length - 1] || node.snapshot || {
          investment: 0,
          profitLoss: 0,
          totalValue: 0,
          returnRate: 0,
          updatedAt: null
        };
        const openingValue = Number(openingSnapshot?.totalValue) || 0;
        const closingValue = Number(closingSnapshot.totalValue) || 0;
        const openingInvestment = Number(openingSnapshot?.investment) || 0;
        const closingInvestment = Number(closingSnapshot.investment) || 0;
        const freshCapital = closingInvestment - openingInvestment;
        const periodProfitLoss = closingValue - openingValue - freshCapital;
        const returnRate = openingValue ? periodProfitLoss / openingValue : 0;

        const childMap = node.children instanceof Map ? node.children : new Map();

        return {
          key: node.key,
          label: node.label,
          investment: freshCapital,
          profitLoss: periodProfitLoss,
          totalValue: closingValue,
          returnRate,
          updatedAt: closingSnapshot.updatedAt,
          children: sortChildren(childMap).map((child) => mapNode(child, sortChildren))
        };
      };

      const sortMapValues = (map) =>
        Array.from(map.values()).sort((a, b) => {
          const aTime = a.snapshot?.timestamp || 0;
          const bTime = b.snapshot?.timestamp || 0;
          return bTime - aTime;
        });

      return Array.from(years.values())
        .sort((a, b) => Number(b.label) - Number(a.label))
        .map((yearNode) => mapNode(yearNode, sortMapValues));
    }
  },

  methods: {
    extractArray(payload, key) {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.[key])) return payload[key];
      if (Array.isArray(payload?.data?.[key])) return payload.data[key];
      return [];
    },

    parsePlatformOptions() {
      const raw = this.platformOptions && typeof this.platformOptions === 'object' && 'value' in this.platformOptions
        ? this.platformOptions.value
        : this.platformOptions;

      if (!raw) return [];
      if (Array.isArray(raw)) return raw;

      try {
        return JSON.parse(raw);
      } catch (error) {
        return typeof raw === 'string'
          ? raw.split(',').map(item => item.trim()).filter(Boolean)
          : [];
      }
    },

    returnClass(value) {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return 'is-flat';
      }
      if (value > 0) return 'text-success';
      if (value < 0) return 'text-error';
      return 'is-flat';
    },

    formatRate(value) {
      return value === null || value === undefined || Number.isNaN(value)
        ? '-'
        : this.$helper.formatPercentage(value);
    },

    calculateXnpv(rate, cashFlows) {
      const firstDate = cashFlows[0].date;
      return cashFlows.reduce((sum, flow) => {
        const years = (flow.date - firstDate) / (365 * 24 * 60 * 60 * 1000);
        return sum + (flow.amount / Math.pow(1 + rate, years));
      }, 0);
    },

    calculateXirr(cashFlows) {
      if (!cashFlows.length) return null;

      const hasPositive = cashFlows.some((flow) => flow.amount > 0);
      const hasNegative = cashFlows.some((flow) => flow.amount < 0);
      if (!hasPositive || !hasNegative) {
        return null;
      }

      let low = -0.9999;
      let high = 10;
      let npvLow = this.calculateXnpv(low, cashFlows);
      let npvHigh = this.calculateXnpv(high, cashFlows);
      let expandCount = 0;

      while (npvLow * npvHigh > 0 && expandCount < 20) {
        high *= 2;
        npvHigh = this.calculateXnpv(high, cashFlows);
        expandCount += 1;
      }

      if (npvLow * npvHigh > 0) {
        return null;
      }

      for (let i = 0; i < 100; i += 1) {
        const mid = (low + high) / 2;
        const npvMid = this.calculateXnpv(mid, cashFlows);

        if (Math.abs(npvMid) < 0.0001) {
          return mid;
        }

        if (npvLow * npvMid < 0) {
          high = mid;
        } else {
          low = mid;
          npvLow = npvMid;
        }
      }

      return (low + high) / 2;
    },

    formatShortDate(value) {
      if (!value) return '-';
      const timestamp = toTimestamp(value);
      if (!Number.isFinite(timestamp)) {
        return '-';
      }
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(new Date(timestamp));
    }
  },

  setup() {
    const store = platformStore();
    const { platform } = storeToRefs(store);
    return { platformOptions: platform };
  },

  apollo: {
    statisticsChart: {
      query: STATISTICS_CHART_QUERY,
      variables() {
        return { platformIds: this.parsePlatformOptions() };
      },
      result({ data }) {
        this.statisticsChart = this.extractArray(data, 'statisticsChart');
      }
    },
    statisticsChartPlatformWise: {
      query: STATISTICS_CHART_PLATFORM_WISE,
      variables() {
        return { platformIds: this.parsePlatformOptions() };
      },
      result({ data }) {
        this.statisticsChartPlatformWise = this.extractArray(data, 'statisticsChartPlatformWise');
      }
    },
    stockTransactionsPlatformWise: {
      query: STOCK_TRANSACTIONS_PLATFORM_WISE,
      variables() {
        return { platformIds: this.parsePlatformOptions() };
      },
      result({ data }) {
        this.stockTransactionsPlatformWise = this.extractArray(data, 'stockTransactionsPlatformWise');
      }
    },
    dividends: {
      query: ALL_DIVIDENDS,
      result({ data }) {
        this.dividends = this.extractArray(data, 'dividends');
      }
    }
  }
};
</script>

<style scoped>
.returns-page {
  padding: 24px;
  display: grid;
  gap: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.page-header h2,
.table-header h3 {
  margin: 0;
  color: var(--dark);
}

.page-header p,
.table-header p {
  margin: 6px 0 0;
  color: var(--text-muted);
}

.snapshot-card,
.metric-card,
.table-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
}

.snapshot-card {
  min-width: 260px;
  padding: 18px 20px;
  display: grid;
  gap: 4px;
}

.snapshot-label,
.metric-card span {
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
}

.snapshot-card strong,
.metric-card strong {
  font-size: 28px;
  line-height: 1.1;
}

.snapshot-card small {
  color: var(--text-muted);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.summary-grid-secondary {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.metric-card {
  padding: 18px 20px;
  display: grid;
  gap: 8px;
}

.metric-card small,
.table-note {
  color: var(--text-muted);
}

.audit-toggle-row {
  padding: 0 4px;
}

.audit-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--dark);
  font-size: 14px;
  font-weight: 600;
}

.audit-toggle input {
  width: 16px;
  height: 16px;
}


.table-card {
  overflow: hidden;
}

.table-header {
  padding: 20px 20px 0;
}

.period-table {
  overflow-x: auto;
  padding: 20px;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;
}

th {
  background: transparent;
  color: var(--text-muted);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f2f4;
  color: var(--dark);
}

.group-row td {
  font-weight: 600;
}

.level-year td {
  background: #fafafa;
}

.level-half td:first-child {
  padding-left: 34px;
}

.level-quarter td:first-child {
  padding-left: 52px;
}

.level-month td:first-child {
  padding-left: 70px;
}

.empty-state {
  padding: 32px 20px;
  color: var(--text-muted);
}

.is-flat {
  color: var(--text-muted);
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid-secondary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .returns-page {
    padding: 16px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .summary-grid-secondary {
    grid-template-columns: 1fr;
  }

  .snapshot-card {
    min-width: 100%;
  }
}
</style>
